import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NAV } from "@/app/nav";

export interface Crumb {
  label: string;
  to?: string;
}

interface BreadcrumbContextValue {
  extra: Crumb[];
  listTo?: string;
  setExtra: (path: string, crumbs: Crumb[], listTo?: string) => void;
  registerListHandler: (fn: () => void) => () => void;
  runListHandlers: () => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const [extra, setExtraState] = useState<{ path: string; crumbs: Crumb[]; listTo?: string }>({
    path: "",
    crumbs: [],
  });
  const listHandlers = useRef(new Set<() => void>());
  const setExtra = useCallback((path: string, crumbs: Crumb[], listTo?: string) => {
    setExtraState({ path, crumbs, listTo });
  }, []);
  const registerListHandler = useCallback((fn: () => void) => {
    listHandlers.current.add(fn);
    return () => {
      listHandlers.current.delete(fn);
    };
  }, []);
  const runListHandlers = useCallback(() => {
    for (const fn of [...listHandlers.current]) fn();
  }, []);

  const value = useMemo(
    () => ({
      extra: extra.path === pathname ? extra.crumbs : [],
      listTo: extra.path === pathname ? extra.listTo : undefined,
      setExtra,
      registerListHandler,
      runListHandlers,
    }),
    [extra, pathname, setExtra, registerListHandler, runListHandlers],
  );
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/** Extra crumbs after the nav item — document number, "new", "edit". */
export function usePageCrumbs(crumbs: Crumb[], listTo?: string): void {
  const setExtra = useContext(BreadcrumbContext)?.setExtra;
  const { pathname } = useLocation();
  const serial = JSON.stringify(crumbs);
  useEffect(() => {
    if (!setExtra) return;
    setExtra(pathname, JSON.parse(serial) as Crumb[], listTo);
    return () => setExtra(pathname, []);
  }, [setExtra, serial, pathname, listTo]);
}

/** Close an on-page panel when the user clicks the list crumb on the same URL. */
export function useListCrumb(handler: () => void): void {
  const register = useContext(BreadcrumbContext)?.registerListHandler;
  useEffect(() => {
    if (!register) return;
    return register(handler);
  }, [register, handler]);
}

export function useRunListCrumb(): () => void {
  return useContext(BreadcrumbContext)?.runListHandlers ?? (() => undefined);
}

export function matchNavItem(pathname: string) {
  let best: { groupKey: string; itemKey: string; to: string } | null = null;
  for (const group of NAV) {
    for (const item of group.items) {
      const ok =
        item.to === "/"
          ? pathname === "/"
          : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (ok && (!best || item.to.length > best.to.length)) {
        best = { groupKey: group.key, itemKey: item.key, to: item.to };
      }
    }
  }
  return best;
}

export function useBreadcrumbTrail(): Crumb[] {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const context = useContext(BreadcrumbContext);
  const extra = context?.extra ?? [];
  const match = matchNavItem(pathname);
  if (pathname === "/releases") return [{label:t("releases.title")}];
  if (!match) return [];

  const onList = pathname === match.to;
  const trail: Crumb[] = [
    { label: t(`nav.${match.groupKey}`) },
    {
      label: t(`nav.${match.itemKey}`),
      to: extra.length > 0 || !onList ? context?.listTo ?? match.to : undefined,
    },
  ];

  // Trang tự đặt tên chính nó làm mảnh đầu (Tình trạng PI, KPI, Cổng nhà cung cấp, Quy trình P —
  // 18/09/2026) mà chữ trùng mục menu thì bỏ, không hiện "X › X". Gỡ ở đây để trang làm sau cũng không lặp.
  const own = extra.length > 0 && extra[0]!.label === trail[1]!.label ? extra.slice(1) : extra;
  if (own.length !== extra.length && own.length === 0) {
    trail[1] = { label: trail[1]!.label, to: onList ? undefined : context?.listTo ?? match.to };
    return trail;
  }
  if (own.length > 0) {
    trail.push(...own);
    return trail;
  }

  const rest = match.to === "/" ? "" : pathname.slice(match.to.length);
  const segs = rest.split("/").filter(Boolean);
  if (segs[0] === "new") trail.push({ label: t("common.new") });
  else if (segs.includes("edit")) trail.push({ label: t("common.edit") });
  else if (segs.length > 0) trail.push({ label: t("common.loading") });
  return trail;
}
